import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Skeleton } from "@/components/ui/skeleton";
import { Heart, MapPin, GraduationCap, Briefcase, BookOpen, ShieldCheck, Sparkles } from "lucide-react";

const PROD = process.env.REACT_APP_PROD_API_URL;

function Row({ icon: Icon, children }) {
  if (!children) return null;
  return <p className="flex items-center gap-1.5 text-sm text-slate-600"><Icon className="h-3.5 w-3.5 shrink-0 text-emerald-700" /> {children}</p>;
}

export default function FamilyView() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${PROD}/family/view/${token}`); // public, no auth
        if (active) { setData(data); setState("ok"); }
      } catch { if (active) setState("error"); }
    })();
    return () => { active = false; };
  }, [token]);

  const member = data?.member;
  const profiles = data?.profiles || [];

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-emerald-900/10 bg-ivory/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/" data-testid="family-home-link"><Logo className="h-9" /></Link>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-mint px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800"><ShieldCheck className="h-3.5 w-3.5" /> Family view — read only</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {state === "loading" && (
          <div data-testid="family-loading">
            <Skeleton className="mb-6 h-24 rounded-3xl" />
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-80 rounded-3xl" />)}</div>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-amber-300 bg-white py-20 text-center" data-testid="family-error">
            <ShieldCheck className="mb-3 h-10 w-10 text-amber-500" />
            <h1 className="font-serif text-2xl font-bold text-emerald-950">This family link is unavailable</h1>
            <p className="mt-2 max-w-sm text-slate-600">This link is invalid or has been turned off. Please ask the member for a new link.</p>
          </div>
        )}

        {state === "ok" && (
          <>
            {/* Member header */}
            <div className="mb-8 flex items-center gap-4 rounded-3xl border border-emerald-900/10 bg-gradient-to-br from-emerald-800 to-emerald-950 p-5 text-white sm:p-6" data-testid="family-member-header">
              {member?.photoBase64 ? (
                <img src={member.photoBase64} alt={member.firstName || "Member"} className="h-16 w-16 rounded-2xl object-cover ring-2 ring-amber-300/40" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10"><Heart className="h-7 w-7 text-amber-300" /></div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-200">Suggested matches for</p>
                <h1 className="font-serif text-2xl font-bold">{member?.firstName || member?.displayName || "Member"}{member?.age ? `, ${member.age}` : ""}</h1>
                {member?.country && <p className="flex items-center gap-1 text-sm text-emerald-100/80"><MapPin className="h-3.5 w-3.5" /> {member.country}</p>}
              </div>
            </div>

            {profiles.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-emerald-300 bg-white py-16 text-center text-slate-500" data-testid="family-empty">No suggested matches to show yet.</div>
            ) : (
              <>
                <p className="mb-4 text-sm text-slate-500" data-testid="family-count">{data.count ?? profiles.length} suggested {profiles.length === 1 ? "match" : "matches"} · read-only</p>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="family-profiles">
                  {profiles.map((p) => (
                    <div key={p.id} className="overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm" data-testid={`family-profile-${p.id}`}>
                      <div className="relative aspect-[4/5] bg-emerald-950/5">
                        {p.photoBase64 ? (
                          <img src={p.photoBase64} alt={p.firstName || "Profile"} className="h-full w-full select-none object-cover" draggable={false} />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center"><Heart className="h-12 w-12 text-emerald-200" /></div>
                        )}
                        {typeof p.matchScore === "number" && (
                          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-bold text-white shadow"><Sparkles className="h-3.5 w-3.5" /> {p.matchScore}%</span>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-serif text-lg font-bold text-emerald-950">{p.firstName || p.displayName || "Member"}{p.age ? `, ${p.age}` : ""}</h3>
                        <div className="mt-1.5 space-y-1">
                          <Row icon={MapPin}>{[p.city, p.country].filter(Boolean).join(", ")}</Row>
                          <Row icon={Briefcase}>{p.profession}</Row>
                          <Row icon={GraduationCap}>{p.education}</Row>
                          <Row icon={BookOpen}>{p.sect}</Row>
                        </div>
                        {Array.isArray(p.matchReasons) && p.matchReasons.length > 0 && (
                          <div className="mt-3 border-t border-emerald-900/10 pt-3">
                            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-600">Why it's a match</p>
                            <ul className="space-y-1">
                              {p.matchReasons.slice(0, 3).map((r, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600"><Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /> {r}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <p className="mt-8 text-center text-xs text-slate-400">This is a private, read-only view shared by the member. Contact details and interest actions are not available here.</p>
          </>
        )}
      </main>
    </div>
  );
}
